import { Controller, UseFormReturn } from 'react-hook-form';
import Input from '../atoms/Input';
import RadioButton from '../atoms/RadioButton';
import config from '../../config/config';
import { CheckoutFormData } from './checkoutValidation';

interface CheckoutContactFormProps {
  form: UseFormReturn<CheckoutFormData>;
  showPhoneOption: boolean;
  isBackoffice: boolean;
  disabled?: boolean;
}

/**
 * CheckoutContactForm - Contact method selection with email/phone inputs
 *
 * Theme Support: ✅ Full light/dark mode support
 * Colors Used: Uses semantic colors via Input and RadioButton components
 */
const CheckoutContactForm: React.FC<CheckoutContactFormProps> = ({
  form,
  showPhoneOption,
  isBackoffice,
  disabled = false,
}) => {
  const {
    control, formState, getValues, setValue, clearErrors,
  } = form;

  const renderEmailFields = () => (
    <>
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <Input
            label="Correo electrónico"
            id="email"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.email?.message}
            labelAbove
          />
        )}
      />
      <Controller
        name="emailConfirm"
        control={control}
        render={({ field }) => (
          <Input
            label="Confirmar correo electrónico"
            id="emailConfirm"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            autoComplete={
              config.production && !isBackoffice
                ? 'idontwantthistoeverautocompleteokok??'
                : 'email'
            }
            onPaste={(e) => (config.production && !isBackoffice ? e.preventDefault() : e)}
            errMsg={formState.errors.emailConfirm?.message}
            labelAbove
          />
        )}
      />
    </>
  );

  const renderPhoneFields = () => (
    <>
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <Input
            label="Numero de celular"
            id="phone"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.phone?.message}
            labelAbove
            type="phone"
          />
        )}
      />
      <Controller
        name="phoneConfirm"
        control={control}
        render={({ field }) => (
          <Input
            label="Confirmar numero de celular"
            id="phoneConfirm"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.phoneConfirm?.message}
            labelAbove
            type="phone"
          />
        )}
      />
    </>
  );

  if (showPhoneOption) {
    return (
      <div className="glass-surface flex flex-col items-center self-stretch p-3">
        <div className="flex flex-col gap-4 items-start self-stretch">
          <h1 className="text-lg font-semibold">Contacto</h1>
          <div className="flex flex-col gap-4 w-full">
            <Controller
              name="contactMethod"
              control={control}
              render={({ field }) => (
                <>
                  <RadioButton
                    name="contactMethod"
                    value="email"
                    checked={field.value === 'email'}
                    description="Recibirás las entradas en tu correo electrónico."
                    label="Correo electrónico"
                    disabled={disabled}
                    onChange={() => {
                      field.onChange('email');
                      setValue('phone', '');
                      setValue('phoneConfirm', '');
                      clearErrors(['phone', 'phoneConfirm']);
                    }}
                  />
                  <RadioButton
                    name="contactMethod"
                    value="phone"
                    checked={field.value === 'phone'}
                    description="Recibirás las entradas por WhatsApp."
                    label="Número de celular"
                    disabled={disabled}
                    onChange={() => {
                      field.onChange('phone');
                      setValue('email', '');
                      setValue('emailConfirm', '');
                      clearErrors(['email', 'emailConfirm']);
                    }}
                  />
                </>
              )}
            />
            {getValues('contactMethod') === 'phone' && renderPhoneFields()}
            {getValues('contactMethod') === 'email' && renderEmailFields()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-surface flex flex-col items-center self-stretch p-3">
      <div className="flex flex-col gap-4 items-start self-stretch">
        <h1 className="text-lg font-semibold">Contacto</h1>
        <div className="flex flex-col gap-4 w-full">{renderEmailFields()}</div>
      </div>
    </div>
  );
};

export default CheckoutContactForm;
